"use client";

import Link from "next/link";
import { Github, X, Linkedin, MessageCircle } from "lucide-react";
import Image from "next/image";

export function Footer() {
  return (
    <footer className="border-t bg-blue-50/60 dark:bg-blue-950/40 backdrop-blur-md">
      <div className="mx-auto w-full max-w-screen-2xl px-4 py-16">
        <div className="mb-12 text-center">
          <p className="text-lg text-muted-foreground italic max-w-2xl mx-auto">
            "Great documentation is where creativity meets clarity, and AI is the bridge that connects them. Together, we're revolutionizing how knowledge is shared."
          </p>
        </div>
        <div className="grid grid-cols-1 gap-8 md:grid-cols-5 md:justify-items-center md:pl-12">
          {/* First column - Logo and social links */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center">
              <div className="h-12 w-12 bg-transparent rounded-md flex items-center justify-center">
                <Image
                  src="/images/genwave-logo.svg" 
                  alt="Genwave Logo"
                  width={48}
                  height={48}
                  className="w-12 h-12 -ml-3"
                />
              </div>
              <span className="text-xl font-semibold">Genwave LLC</span>
            </div>
            <div className="flex gap-4">
              <Link
                href=""
                target="_blank"
                rel="noreferrer"
                className="rounded-full bg-black p-1.5 text-white hover:bg-gray-900"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Twitter</span>
              </Link>
              <Link
                href=""
                target="_blank"
                rel="noreferrer"
                className="rounded-full bg-black p-1.5 text-white hover:bg-gray-900"
              >
                <Github className="h-4 w-4" />
                <span className="sr-only">GitHub</span>
              </Link>
              <Link
                href=""
                target="_blank"
                rel="noreferrer"
                className="rounded-full bg-black p-1.5 text-white hover:bg-gray-900"
              >
                <Linkedin className="h-4 w-4" />
                <span className="sr-only">LinkedIn</span>
              </Link>
              <Link
                href=""
                target="_blank"
                rel="noreferrer"
                className="rounded-full bg-black p-1.5 text-white hover:bg-gray-900"
              >
                <MessageCircle className="h-4 w-4" />
                <span className="sr-only">Discord</span>
              </Link>
            </div>
          </div>

          {/* Second column - Products */}
          <div className="flex flex-col gap-4">
            <h3 className="text-base font-semibold">Products</h3>
            <div className="flex flex-col gap-3">
              <Link
                href="/docs"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Docs
              </Link>
              <Link
                href="/api-playground"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Playground
              </Link>
              <Link
                href="/chat"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Chat
              </Link>
              <button
                onClick={() => {
                  // If already on home page, scroll smoothly
                  if (window.location.pathname === '/') {
                    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
                  } else {
                    // If on another page, navigate to home page with pricing hash
                    window.location.href = '/#pricing';
                  }
                }}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors text-left"
              >
                Pricing
              </button>
              <Link
                href="/changelog"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Changelog
              </Link>
            </div>
          </div>
          {/* Third column - API Documentation */}
          <div className="flex flex-col gap-4">
            <h3 className="text-base font-semibold">API Documentation</h3>
            <div className="flex flex-col gap-3">
              <Link
                href="#"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Getting started
              </Link>
              <Link
                href="#"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                API Reference
              </Link>
              <Link
                href="#"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Examples
              </Link>
              <Link
                href="#"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                SDKs
              </Link>
            </div>
          </div>
          {/* Fourth column - Company */}
          <div className="flex flex-col gap-4">
            <h3 className="text-base font-semibold">Company</h3>
            <div className="flex flex-col gap-3">
              <Link
                href="/blog"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Blog
              </Link>
              <Link
                href="#"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Careers
              </Link>
              <Link
                href="/api-status"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                API Status
              </Link>
            </div>
          </div>
          {/* Fifth column - Legal */}
          <div className="flex flex-col gap-4">
            <h3 className="text-base font-semibold">Legal</h3>
            <div className="flex flex-col gap-3">
              <Link
                href="/legal/terms"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Terms of Service
              </Link>
              <Link
                href="/legal/privacy"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Privacy Policy
              </Link>
            </div>
          </div>
        </div>
        
        {/* reCAPTCHA notice */}
        <div className="mt-8 text-center">
          <p className="recaptcha-notice">
            This site is protected by reCAPTCHA and the Google{' '}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-primary">
              Privacy Policy
            </a>{' '}
            and{' '}
            <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-primary">
              Terms of Service
            </a>{' '}
            apply.
          </p>
        </div>
      </div>
    </footer>
  );
}
